<template>
  <v-dialog
    v-model="open"
    max-width="400"
    scrollable
  >
    <v-card style="max-height: 70vh; overflow-y: auto;">
      <v-card-title>適用ユーザー管理</v-card-title>
      <v-card-text>
        <v-list v-if="aggregationUsers.length > 0">
          <v-list-item
            v-for="(aggregationUser, index) in aggregationUsers"
            :key="`assigned-user-${aggregationUser.attributes.id}-${index}`"
          >
            <v-list-item-content>
              <v-list-item-title>
                {{ aggregationUser.attributes.user.data.attributes.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                icon
                :disabled="loading"
                @click="destroy(aggregationUser)"
              >
                <v-icon small>
                  {{ icons.mdiClose }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <p
          v-else
          class="text--disabled text-body-2 mb-0"
        >
          適用ユーザーがありません
        </p>

        <v-divider class="my-4" />

        <v-combobox
          :value="selectedUserName"
          :items="availableUsers"
          item-text="attributes.name"
          label="新しくユーザーを紐付ける"
          placeholder="ユーザーを選択してください"
          no-data-text="利用可能なユーザーがありません"
          :disabled="loading"
          @focus="onFetchAvailableUsers"
          @change="onUserSelect"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="open = false"
        >
          閉じる
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref, toRefs, computed,
} from '@vue/composition-api'
import { mdiClose } from '@mdi/js'

/**
 * 適用ユーザーの変更ダイアログ（新クロージングページ用）。
 *
 * 既存 `eod-closing/components/AggregationUsersDialogListItem.vue` を参考に、
 * v-list-item 兼アクティベーターだった構成を、外部から v-model で開閉する
 * 独立ダイアログとして再構成している。
 */
export default {
  name: 'AggregationUsersDialog',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    // ダイアログ開閉状態
    value: {
      type: Boolean,
      default: false,
    },
    // 適用済みユーザー（aggregationUsers.data）
    aggregationUsers: {
      type: Array,
      default: () => [],
    },
    // 適用候補ユーザー（availableUsers）
    availableUsers: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const { availableUsers } = toRefs(props)

    const selectedUserId = ref(null)
    const loading = ref(false)

    const open = computed({
      get: () => props.value,
      set: val => emit('input', val),
    })

    const selectedUserName = computed(() => {
      if (!selectedUserId.value) return ''

      return availableUsers.value.find(
        user => user.attributes.id === selectedUserId.value,
      )?.attributes?.name
    })

    const onFetchAvailableUsers = () => {
      if (loading.value) return

      loading.value = true
      const callback = () => { loading.value = false }
      emit('fetch-available-users', callback)
    }

    const onUserSelect = item => {
      if (loading.value) return
      if (!item?.attributes?.id) return

      loading.value = true
      const callback = () => {
        loading.value = false
        selectedUserId.value = null
      }
      selectedUserId.value = item.attributes.id
      emit('create', { userId: item.attributes.id, callback })
    }

    const destroy = aggregationUser => {
      if (loading.value) return

      loading.value = true
      const callback = () => { loading.value = false }
      emit('destroy', {
        id: aggregationUser.attributes.id,
        userId: aggregationUser.attributes.userId,
        callback,
      })
    }

    return {
      open,
      loading,
      selectedUserName,
      onFetchAvailableUsers,
      onUserSelect,
      destroy,
      icons: {
        mdiClose,
      },
    }
  },
}
</script>
